import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import { debounce } from 'underscore';
import { createPortal } from 'react-dom';

import { Spinner } from './Spinner';

const getSuggestionValue = suggestion => suggestion.text;

const AutoComplete = forwardRef(
  (
    {
      clearSuggestion,
      initValue = '',
      loading = false,
      loadSuggestion,
      pickSuggestion,
      selectSuggestion,
      suggestions = [],
      placeholder = 'Input',
      name = 'autosuggestion',
      rowData,
      rowNode,
    },
    ref,
  ) => {
    const [value, setValue] = useState(initValue);
    const inputRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
      const input = inputRef.current;

      if (input) {
        const timer = setTimeout(() => input.select(), 100);

        return () => {
          clearTimeout(timer);
        };
      }
    }, []);

    const renderSuggestion = useCallback(
      suggestion => (
        <div className="react-autosuggest__suggestion-wrapper">
          <span className="ind--text">{suggestion.text}</span>
        </div>
      ),
      [],
    );

    const handleSuggestionsFetchRequested = useCallback(
      ({ value: inputValue, reason }) => {
        if (reason === 'input-changed' || reason === 'input-focused') {
          debounce(loadSuggestion({ value: inputValue, rowData, rowNode }), 500);
        }
      },
      [rowData, rowNode],
    );

    const handleSuggestionsClearRequested = useCallback(() => {
      clearSuggestion({ rowData, rowNode });
    }, [rowData, rowNode]);

    const handleChange = useCallback((event, { newValue }) => {
      setValue(newValue);
    }, []);

    const handleSuggestionSelected = useCallback(
      (event, { suggestion }) => {
        pickSuggestion({
          rowData,
          rowNode,
          selected: suggestion,
          inputText: value,
        });
      },
      [rowData, rowNode, value],
    );

    const renderSuggestionsContainer = useCallback(
      ({ containerProps, children }) => {
        const targetElement = panelRef.current;

        if (!targetElement) return null;

        const bodyRect = document.documentElement.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const top = targetRect.bottom - bodyRect.top - 3;
        const left = targetRect.left - bodyRect.left;
        const width = Math.max(targetRect.width, 150);

        const style = {
          position: 'absolute',
          top,
          left,
          width,
          border: '1px solid gray',
          background: '#FFF',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1060,
        };

        const loader = loading && (
          <ul className="react-autosuggest__suggestions-list">
            <li className="react-autosuggest__suggestion">
              <Spinner size="small" inline /> Loading...
            </li>
          </ul>
        );

        const { key, ...restContainerProps } = containerProps;

        const container = (
          <div {...restContainerProps} key={key} className="autocomplete-div-hide-on-empty" style={style}>
            {loader}
            {children}
          </div>
        );

        return createPortal(container, document.querySelector('body'));
      },
      [loading],
    );

    const renderInputComponent = useCallback(inputProps => {
      const { ref, key, onKeyDown, ...restProps } = inputProps;

      inputRef.current = typeof ref === 'function' ? null : ref?.current;

      return (
        <input
          key={key}
          {...restProps}
          ref={element => {
            inputRef.current = element;
            if (typeof ref === 'function') ref(element);
            else if (ref) ref.current = element;
          }}
          onKeyDown={event => {
            // Let Autosuggest handle the key first (e.g. Enter to select suggestion)
            if (onKeyDown) onKeyDown(event);
            // Stop ag-grid from handling these keys
            if ([13, 32, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) > -1) {
              event.stopPropagation();
            }
          }}
        />
      );
    }, []);

    const inputProps = {
      className: 'form-control no-border',
      name,
      disabled: false,
      placeholder,
      onChange: handleChange,
      value,
    };

    useImperativeHandle(
      ref,
      () => ({
        selectSuggestion,
      }),
      [selectSuggestion],
    );

    return (
      <div ref={panelRef}>
        <Autosuggest
          getSuggestionValue={getSuggestionValue}
          inputProps={inputProps}
          onSuggestionsClearRequested={handleSuggestionsClearRequested}
          onSuggestionSelected={handleSuggestionSelected}
          onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
          renderInputComponent={renderInputComponent}
          renderSuggestion={renderSuggestion}
          renderSuggestionsContainer={renderSuggestionsContainer}
          suggestions={suggestions}
        />
      </div>
    );
  },
);

AutoComplete.propTypes = {
  clearSuggestion: PropTypes.func.isRequired,
  initValue: PropTypes.string,
  loading: PropTypes.bool,
  loadSuggestion: PropTypes.func.isRequired,
  pickSuggestion: PropTypes.func.isRequired,
  suggestions: PropTypes.array,
};

export default function(connect) {
  const Editor = connect(AutoComplete);

  const AutoCompleteCellEditor = forwardRef((props, ref) => {
    const { api, column, node, value, clearSuggestion, loadSuggestion, initValue } = props;
    const suggestionRef = useRef(null);
    const panelRef = useRef(null);
    const suggestionData = useRef(null);

    const stopEditing = useCallback(() => {
      api.stopEditing();
    }, [api]);

    const stopPropagation = useCallback(event => {
      event.stopPropagation();
    }, []);

    useEffect(() => {
      document.addEventListener('click', stopEditing);

      const panel = panelRef.current;

      if (panel) {
        panel.addEventListener('click', stopPropagation);
      }

      return () => {
        document.removeEventListener('click', stopEditing);
        if (panel) {
          panel.removeEventListener('click', stopPropagation);
        }
      };
    }, [stopEditing, stopPropagation]);

    const pickSuggestion = useCallback(
      suggestion => {
        const { selected, inputText } = suggestion;

        if (selected.id === null || selected.id === undefined || selected.id === -1) {
          if (column.colDef.addNewItem) {
            column.colDef.addNewItem({ rowNode: node, inputText });
          }
        } else {
          suggestionData.current = suggestion;
        }
        stopEditing();
      },
      [column, node, stopEditing],
    );

    useImperativeHandle(ref, () => ({
      getValue() {
        return suggestionData.current ? suggestionData.current.selected.text : initValue;
      },

      isPopup() {
        return false;
      },

      isCancelAfterEnd() {
        if (suggestionData.current && suggestionRef.current) {
          suggestionRef.current.selectSuggestion(suggestionData.current);

          return false;
        }

        return true;
      },
    }));

    return (
      <div ref={panelRef} className="all-h100p">
        <Editor
          ref={suggestionRef}
          clearSuggestion={clearSuggestion}
          initValue={value || ''}
          loadSuggestion={loadSuggestion}
          pickSuggestion={pickSuggestion}
          rowData={node.data}
          rowNode={node}
        />
      </div>
    );
  });

  return AutoCompleteCellEditor;
}
